import React from "react";
import { CssBaseline, ThemeProvider } from "@mui/material";
import { useSelector } from "react-redux";
import { useRoutes, useNavigate } from "react-router-dom";
import "./App.css";
import { RTL } from "./layout/fullLayout/customizer/RTL";
import { ThemeSettings } from "./layout/fullLayout/customizer/ThemeSettings";
import { IStoreState } from "./redux/initialStoreState";
import { Router } from "./router/router";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { AdapterMoment } from "@mui/x-date-pickers/AdapterMoment";
import {
  DataTableV2Provider,
  DataTableV2ProviderHandle,
} from "./components/TableV2/context/DataTableV2Provider";
import { api } from "./api/api";
import { useAuth } from "./contexts/AuthProvider/AuthProvider";
import { navigationService } from "./services/navigationService";
import { WhatsAppStatusProvider } from "./contexts/WhatsAppStatusProvider";
import { MessageBox } from "./components/MessageBox/MessageBox";
import { Helmet } from "react-helmet";

function App() {
  const routing = useRoutes(Router);
  const navigate = useNavigate();
  const theme = ThemeSettings();
  const customizer = useSelector(
    (state: IStoreState) => state.customizerReducer
  );
  const ref = React.useRef<DataTableV2ProviderHandle | null>(null);
  const { user } = useAuth();

  // Initialize navigation service
  React.useEffect(() => {
    navigationService.setNavigateFunction(navigate);
  }, [navigate]);

  const getCachingData = async () => {
    try {
      const res = await api.get(
        "/user/get-user-meta-config?user_uuid=" + user.userUuid
      );
      if (res.data.data.length > 0) {
        return res.data.data[0]["config_value"];
      }
      return {};
    } catch (err) {
      console.log(err);
      return {};
    }
  };

  const saveCachingData = async (data: any) => {
    try {
      await api.post("/user/upsert-user-meta-config", {
        user_uuid: user.userUuid,
        module_name: "",
        config_value: data,
      });
    } catch (err) {
      console.log(err);
    }
  };

  React.useEffect(() => {
    if (user.isLogin && user.tokens) {
      if (ref.current && ref.current.getCachingConfigFromServerAtLoad) {
        ref.current.getCachingConfigFromServerAtLoad();
      }
    }
  }, [user]);

  return (
    <>
      <Helmet>
        <title>{process.env.REACT_APP_TITLE || "Banquet First"}</title>
        <meta
          name="description"
          content={
            process.env.REACT_APP_SUB_TITLE ||
            "Spaces Smarter with Banquet First"
          }
        />
      </Helmet>

      <ThemeProvider theme={theme}>
        <LocalizationProvider dateAdapter={AdapterMoment}>
          <RTL direction={customizer.activeDir as "ltr"}>
            <DataTableV2Provider
              ref={ref}
              cachingFilters={{
                enableCaching: true,
                getCachingConfigFromServerAtLoad: getCachingData,
                saveCachingIntoServer: saveCachingData,
              }}
            >
              <WhatsAppStatusProvider>
                <CssBaseline />

                {routing}
              </WhatsAppStatusProvider>
            </DataTableV2Provider>
          </RTL>
        </LocalizationProvider>
        <MessageBox />
      </ThemeProvider>
    </>
  );
}

export default App;
